import React from "react";
import styles from "../Table.module.css";
import {
  FilterTag,
  propsForValue,
} from "src/search/components/inputs/FilterTag";
import { SearchContext } from "src/search/useSearchContext";
import { CellFormatterOptions } from "../formatters/types";
import { formatters } from "../formatters/cell-formatters";

export interface TableCellProps {
  value: any;
  options: CellFormatterOptions;
  record?: any;
  onClick?: (value: any, record: any) => void;
  className?: string;
  isExpandable?: boolean;
  isExpanded?: boolean;
  onExpand?: (expanded: boolean) => void;
  level?: number;
  tooltipContent?: React.ReactNode;
  context?: SearchContext;
  properties?: Array<{ target: string; attribute: string }>;
  rowCount?: number;
  showRollup?: boolean;
  renderTooltip?: (content: React.ReactNode) => React.ReactNode;
}

export const TableCell: React.FC<TableCellProps> = ({
  value,
  options,
  record,
  onClick,
  className,
  isExpandable,
  isExpanded,
  onExpand,
  level = 0,
  context,
  properties,
  rowCount,
  showRollup,
  renderTooltip,
}) => {
  // Fall back to existing formatter pattern
  const formattedCell = formatters[options.type || "group"](
    value,
    options,
    record
  );

  // Helper to determine if we're dealing with an aggregation context
  const hasAggregationContext = record?.aggregations?.length > 0;

  const mainContent = formattedCell.isHtml ? (
    <div
      dangerouslySetInnerHTML={{ __html: formattedCell.displayValue as string }}
    />
  ) : options.type === "group" ? (
    <>
      {context && properties ? (
        <FilterTag
          context={context}
          {...propsForValue(
            formattedCell.rawValue,
            record,
            record.field,
            context
          )}
          options={{
            showFilters: true,
            showGroupBy: true,
            showAggregate: false,
            showDrillDown: true,
            showEntityOpener: true,
            showValueCopiers: false,
          }}
        />
      ) : (
        <span className={styles.label}>{formattedCell.displayValue}</span>
      )}
    </>
  ) : (
    <div className={styles.measureContent}>
      {context && properties ? (
        <FilterTag
          context={context}
          {...propsForValue(
            formattedCell.rawValue,
            record,
            record.field,
            context
          )}
          values={[
            {
              value: formattedCell.rawValue,
              label: `${options.prefix || ""}${formattedCell.displayValue}${
                options.suffix || ""
              }`,
            },
          ]}
          options={{
            showFilters: true,
            showGroupBy: hasAggregationContext ? false : true,
            showPivot: hasAggregationContext ? false : true,
            showSort: hasAggregationContext ? false : true,
            showAggregate: hasAggregationContext,
            showDrillDown: true,
            showEntityOpener: true,
            showValueCopiers: false,
          }}
        />
      ) : (
        <>
          {options.prefix && (
            <span className={styles.prefix}>{options.prefix}</span>
          )}
          <span className={styles.value}>{formattedCell.displayValue}</span>
          {options.suffix && (
            <span className={styles.suffix}>{options.suffix}</span>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className={styles.row_header_content}>
      <div style={{ display: "inline-flex", maxWidth: "fit-content" }}>
        {options.showTooltip && renderTooltip
          ? renderTooltip(mainContent)
          : mainContent}
      </div>
    </div>
  );
};
